import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { captureMessage } from "@sentry/browser"

import { post } from "shared/lib/api"
import { clearCache } from "shared/lib/graphql"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"

import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import SubscriptionHelper from "./trialing_subscription_helper"

import "../payment_info_form.scss"

export default function TrialInfoForm({
  assets,
  campaignId,
  handleSuccessFlow,
  logLocationType,
  planId,
  productId,
  urls,
  handleErrorFlow,
}) {
  const formRef = useRef(null)
  const [handlingSubmit, setHandlingSubmit] = useState(false)

  useEffect(() => {
    logIFTTTNextAction("page_viewed", {
      location_type: logLocationType,
      object_status: "standard",
      object_id: campaignId,
    })
  }, [])

  useEffect(() => {
    post(urls.createStripeCustomerUrl)
      .then(data => {
        const { errors } = data
        if (errors && errors.length > 0 && window.App.allowDebugErrorMessages) {
          captureMessage("createStripeCustomer failed", { extra: errors })
        }
      })
      .catch(rejectedXHRPromiseLogger())
  }, [])

  const fireGAEvent = step => {
    window.ga && window.ga("send", "event", "ProSubscription", step)
  }

  // Every time we submit payment (stripe, Google or Apple), track this on GA.
  useEffect(() => {
    if (handlingSubmit) {
      fireGAEvent("start")
    }
  }, [handlingSubmit])

  async function handleSubmit(event) {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    logIFTTTNextAction("click", {
      location_type: logLocationType,
      object_type: "start_trial_button",
    })

    await SubscriptionHelper(urls.subscriptionsPath).subscribe(
      planId,
      productId,
      handleSuccessfulSubscription,
      handleError
    )
  }

  function handleSuccessfulSubscription() {
    fireGAEvent("complete")
    clearCache()
    handleSuccessFlow()
  }

  function handleError(error) {
    setHandlingSubmit(false)
    const errorMessage = error.message || error

    logIFTTTNextAction("error", {
      location_type: logLocationType,
      object_type: "start_trial_error",
      object_id: errorMessage.match(/stripe/) ? "stripe_error" : "ifttt_error",
    })
    fireGAEvent("fail")

    window.appendFlash(errorMessage, "error")
    handleErrorFlow(errorMessage)
  }

  let tierName, illustration, copy
  switch (planId) {
    case "pro":
      illustration = assets.proPlusIllustration
      tierName = "Pro+"
      copy = "Create unlimited advanced Applets."
      break
    case "intermediate_pro":
      illustration = assets.proIllustration
      tierName = "Pro"
      copy = "Create up to 20 Applets."
  }

  const title = `Try ${tierName} for free`
  const buttonTitle = `Try now, pay later`
  const submittingCopy = "Submitting..."

  return (
    <>
      <section className="welcome">
        <img src={illustration} alt={`Try ${tierName} for Free`} />
        <h2 className="title">{title}</h2>
        <p>
          {copy} <br />
          Make your favorite things work better together.
          <strong> No credit card required.</strong>
        </p>
      </section>
      <div className="flexbox">
        <form
          ref={formRef}
          styleName="payment-info-form"
          className="form payment-info-form"
          onSubmit={handleSubmit}
        >
          <section styleName="action-buttons">
            <button
              id="submit-payment"
              type="submit"
              className="button-primary pay-later"
              title={buttonTitle}
              aria-label={buttonTitle}
              disabled={handlingSubmit}
            >
              {handlingSubmit ? submittingCopy : buttonTitle}
            </button>
          </section>
        </form>
      </div>
    </>
  )
}

TrialInfoForm.propTypes = {
  assets: PropTypes.shape({
    proIllustration: PropTypes.string.isRequired,
    proPlusIllustration: PropTypes.string, // non required to be used on onboarding
  }),
  campaignId: PropTypes.string,
  planId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  handleSuccessFlow: PropTypes.func.isRequired,
  handleErrorFlow: PropTypes.func,
  logLocationType: PropTypes.string.isRequired,
  urls: PropTypes.shape({
    createStripeCustomerUrl: PropTypes.string.isRequired,
    subscriptionsPath: PropTypes.string.isRequired,
  }),
}

TrialInfoForm.defaultProps = {
  handleErrorFlow: () => {},
}
